import { useMemo, useState } from 'react'
import { Loader2, Tag, Trash2 } from 'lucide-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { usePermissions } from '@/hooks/usePermissions'
import {
  mensajeEtiquetaEnUso,
  useAsignarEtiqueta,
  useCrearEtiqueta,
  useDesasignarEtiqueta,
  useEliminarEtiqueta,
  useEtiquetasCliente,
  useEtiquetasDeCliente,
  useUsosEtiqueta,
} from '@/core/hooks/useEtiquetasCliente'

interface Props {
  clienteId: string
  clienteNombre: string
}

/**
 * Etiquetas del cliente (ORR-364).
 *
 * La etiqueta no es decorativa: una tarea que se repite puede apuntar a ella y generarse para
 * todos los clientes que la lleven, incluidos los que la reciban después. Por eso la línea de
 * ayuda dice qué consecuencia tiene marcarla — sin eso parece una nota de color.
 *
 * Es un atributo del cliente, así que se gobierna con `editarCliente`: sin ese permiso se ven los
 * badges y nada más.
 */
export function EtiquetasClienteCard({ clienteId, clienteNombre }: Props) {
  const { puede } = usePermissions()
  const { data: catalogo = [], isLoading: cargandoCatalogo } = useEtiquetasCliente()
  const { data: asignadas = [], isLoading: cargandoAsignadas } = useEtiquetasDeCliente(clienteId)
  const crear = useCrearEtiqueta()
  const asignar = useAsignarEtiqueta()
  const desasignar = useDesasignarEtiqueta()
  const [nueva, setNueva] = useState('')
  const [administrando, setAdministrando] = useState(false)

  const nombrePorId = useMemo(
    () => new Map(catalogo.map((e) => [e.id, e.nombre])),
    [catalogo]
  )
  const opciones = useMemo(
    () => catalogo.map((e) => ({ value: e.id, label: e.nombre })),
    [catalogo]
  )

  // El multi-select devuelve la lista entera; acá se traduce a las altas y bajas que hubo, que es
  // lo que la base entiende (una fila del puente por par cliente-etiqueta).
  const handleChange = (siguiente: string[]) => {
    // Sets y no `includes`: adentro del filter, `includes` es una búsqueda lineal por elemento —
    // O(n²) sobre el catálogo entero cada vez que se tilda una etiqueta.
    const previas = new Set(asignadas)
    const proximas = new Set(siguiente)

    for (const etiquetaId of siguiente.filter((id) => !previas.has(id))) {
      asignar.mutate({
        clienteId,
        etiquetaId,
        clienteNombre,
        etiquetaNombre: nombrePorId.get(etiquetaId),
      })
    }
    for (const etiquetaId of asignadas.filter((id) => !proximas.has(id))) {
      desasignar.mutate({ clienteId, etiquetaId })
    }
  }

  const handleCrear = async () => {
    const nombre = nueva.trim()
    if (!nombre) return
    try {
      // Crear y asignar en un paso: nadie crea una etiqueta desde la ficha de un cliente para no
      // ponérsela a ese cliente.
      const etiqueta = await crear.mutateAsync(nombre)
      setNueva('')
      asignar.mutate({
        clienteId,
        etiquetaId: etiqueta.id,
        clienteNombre,
        etiquetaNombre: etiqueta.nombre,
      })
    } catch {
      // El toast lo muestra `useCrearEtiqueta`; acá solo se evita la promesa sin capturar.
    }
  }

  if (cargandoCatalogo || cargandoAsignadas) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Cargando etiquetas...
      </div>
    )
  }

  return (
    <div>
      <h3 className="font-semibold mb-3 flex items-center gap-2 text-primary">
        <Tag className="h-4 w-4" />
        Etiquetas
      </h3>

      <div className="flex flex-wrap items-center gap-2 mb-2">
        {asignadas.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin etiquetas.</p>
        ) : (
          asignadas.map((id) => (
            <Badge key={id} variant="secondary">
              {nombrePorId.get(id) ?? 'Etiqueta eliminada'}
            </Badge>
          ))
        )}
      </div>

      {puede.editarCliente && (
        <div className="flex flex-wrap items-center gap-2">
          <MultiSelectFilter
            options={opciones}
            value={asignadas}
            onChange={handleChange}
            label="Etiquetas"
            emptyLabel="ninguna"
            searchable
            searchPlaceholder="Buscar etiqueta..."
          />
          <Input
            value={nueva}
            onChange={(e) => setNueva(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return
              e.preventDefault()
              void handleCrear()
            }}
            placeholder="Nueva etiqueta"
            className="h-9 w-48"
            aria-label="Nombre de la etiqueta nueva"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => void handleCrear()}
            disabled={!nueva.trim() || crear.isPending}
          >
            {crear.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            Crear
          </Button>
          {catalogo.length > 0 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setAdministrando((v) => !v)}
              aria-expanded={administrando}
            >
              {administrando ? 'Listo' : 'Administrar'}
            </Button>
          )}
        </div>
      )}

      {puede.editarCliente && administrando && <AdministrarEtiquetas catalogo={catalogo} />}

      <p className="mt-2 text-xs text-muted-foreground">
        Una tarea que se repite puede apuntar a una etiqueta: se crea una tarea por cada cliente que
        la tenga, incluidos los que la reciban más adelante.
      </p>
    </div>
  )
}

type UsosEtiqueta = Pick<
  ReturnType<typeof useUsosEtiqueta>,
  'isLoading' | 'isError' | 'isSuccess' | 'data'
>

/** Dice en cuántos clientes está puesta la etiqueta y qué pasa al borrarla. */
const textoClientes = (clientes: number): string => {
  if (clientes === 0) return 'No está puesta en ningún cliente.'
  if (clientes === 1) return 'Está puesta en 1 cliente. Si confirmás, se le quita.'
  return `Está puesta en ${clientes} clientes. Si confirmás, se les quita a todos.`
}

/**
 * Textos del diálogo de borrado según el estado del conteo de usos.
 * Solo se puede borrar cuando el conteo terminó y ninguna tarea que se repite usa la etiqueta.
 */
const textosBorrado = (usos: UsosEtiqueta, nombre: string) => {
  if (usos.isLoading) {
    return {
      titulo: `Eliminar «${nombre}»`,
      descripcion: 'Revisando dónde se usa...',
      cargando: true,
      puedeBorrar: false,
      textoCancelar: 'Cancelar',
    }
  }
  if (usos.isError) {
    return {
      titulo: `Eliminar «${nombre}»`,
      descripcion: 'No se pudo revisar dónde se usa la etiqueta. Probá de nuevo en un momento.',
      cargando: false,
      puedeBorrar: false,
      textoCancelar: 'Cancelar',
    }
  }
  const repeticiones = usos.data?.repeticiones ?? 0
  if (repeticiones > 0) {
    return {
      titulo: `No se puede eliminar «${nombre}»`,
      descripcion: mensajeEtiquetaEnUso(repeticiones),
      cargando: false,
      puedeBorrar: false,
      textoCancelar: 'Entendido',
    }
  }
  return {
    titulo: `¿Eliminar la etiqueta «${nombre}»?`,
    descripcion: textoClientes(usos.data?.clientes ?? 0),
    cargando: false,
    puedeBorrar: usos.isSuccess,
    textoCancelar: 'Cancelar',
  }
}

/**
 * Lista del catálogo con un botón para borrar cada etiqueta.
 * Antes de borrar confirma en cuántos clientes está puesta. Si una tarea que se repite la usa
 * como grupo, muestra el bloqueo y no ofrece borrar.
 */
function AdministrarEtiquetas({ catalogo }: { catalogo: { id: string; nombre: string }[] }) {
  const eliminar = useEliminarEtiqueta()
  const [aBorrar, setABorrar] = useState<{ id: string; nombre: string } | null>(null)
  const usos = useUsosEtiqueta(aBorrar?.id)
  const textos = textosBorrado(usos, aBorrar?.nombre ?? '')

  return (
    <>
      <ul className="mt-2 divide-y rounded-md border">
        {catalogo.map((etiqueta) => (
          <li key={etiqueta.id} className="flex items-center justify-between px-3 py-1.5 text-sm">
            <span>{etiqueta.nombre}</span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-muted-foreground hover:text-destructive"
              onClick={() => setABorrar({ id: etiqueta.id, nombre: etiqueta.nombre })}
              aria-label={`Eliminar etiqueta ${etiqueta.nombre}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </li>
        ))}
      </ul>

      <AlertDialog open={!!aBorrar} onOpenChange={(abierto) => !abierto && setABorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{textos.titulo}</AlertDialogTitle>
            <AlertDialogDescription className="flex items-center gap-2">
              {textos.cargando && <Loader2 className="h-4 w-4 animate-spin" />}
              {textos.descripcion}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{textos.textoCancelar}</AlertDialogCancel>
            {textos.puedeBorrar && (
              <AlertDialogAction
                className={buttonVariants({ variant: 'destructive' })}
                onClick={() => {
                  if (aBorrar) eliminar.mutate(aBorrar.id)
                }}
              >
                Eliminar
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
